/**
 * EChartsRenderer — AI 生成的 ECharts 图表渲染器
 *
 * 将 ```echarts 代码块渲染为交互式 Canvas 图表。
 * 支持：柱状图、折线图、饼图、散点图、雷达图、漏斗图、仪表盘等。
 *
 * 用法：在 ReactMarkdown 的 components.code 中，
 * 当检测到 language-echarts 时，渲染此组件。
 *
 * 内置 Institutional Minimalist 品牌色系，与后端 DocEngine 保持一致。
 */

import React, { useEffect, useRef, useState, useId } from 'react';
import * as echarts from 'echarts';

// ─── Institutional Minimalist 品牌色系 ───────────────
// 与后端 chart-render.service.ts 完全一致的 8 色冷色调
const INSTITUTIONAL_COLORS = [
    '#3B82F6', // Blue 500
    '#1E40AF', // Blue 800
    '#6366F1', // Indigo 500
    '#0EA5E9', // Sky 500
    '#8B5CF6', // Violet 500
    '#14B8A6', // Teal 500
    '#F59E0B', // Amber 500
    '#64748B', // Slate 500
];

const INSTITUTIONAL_THEME: Record<string, any> = {
    color: INSTITUTIONAL_COLORS,
    backgroundColor: 'transparent',
    title: {
        textStyle: {
            color: '#1F2937',
            fontSize: 15,
            fontWeight: 'bold',
            fontFamily: 'Inter, "Noto Sans SC", sans-serif',
        },
        subtextStyle: {
            color: '#6B7280',
            fontSize: 12,
            fontFamily: 'Inter, "Noto Sans SC", sans-serif',
        },
    },
    legend: {
        textStyle: {
            color: '#4B5563',
            fontSize: 12,
            fontFamily: 'Inter, "Noto Sans SC", sans-serif',
        },
    },
    textStyle: {
        color: '#374151',
        fontFamily: 'Inter, "Noto Sans SC", sans-serif',
    },
};

// ─── 深度合并 ────────────────────────────────
function deepMerge(target: any, source: any): any {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        if (
            source[key] &&
            typeof source[key] === 'object' &&
            !Array.isArray(source[key]) &&
            target[key] &&
            typeof target[key] === 'object' &&
            !Array.isArray(target[key])
        ) {
            result[key] = deepMerge(target[key], source[key]);
        } else {
            result[key] = source[key];
        }
    }
    return result;
}

// ─── 组件 ────────────────────────────────────

interface EChartsRendererProps {
    /** AI 输出的 ECharts Option JSON 字符串 */
    optionString: string;
}

const EChartsRenderer: React.FC<EChartsRendererProps> = ({ optionString }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.ECharts | null>(null);
    const [error, setError] = useState<string>('');
    const [parsed, setParsed] = useState<boolean>(false);
    const uniqueId = useId().replace(/:/g, '_');

    useEffect(() => {
        if (!optionString.trim() || !chartRef.current) return;

        try {
            // 解析 JSON
            const userOption = JSON.parse(optionString.trim());

            // 合并品牌主题
            const mergedOption = deepMerge(INSTITUTIONAL_THEME, userOption);

            // 确保动画
            if (mergedOption.animation === undefined) {
                mergedOption.animation = true;
                mergedOption.animationDuration = 800;
                mergedOption.animationEasing = 'cubicInOut';
            }

            // 初始化或更新图表
            if (chartInstanceRef.current) {
                chartInstanceRef.current.dispose();
            }

            const chart = echarts.init(chartRef.current, undefined, {
                renderer: 'canvas',
            });
            chartInstanceRef.current = chart;

            chart.setOption(mergedOption);
            setParsed(true);
            setError('');

            // 响应式
            const handleResize = () => chart.resize();
            window.addEventListener('resize', handleResize);

            // Cleanup
            return () => {
                window.removeEventListener('resize', handleResize);
                chart.dispose();
                chartInstanceRef.current = null;
            };
        } catch (err: any) {
            console.error('[ECharts] Render failed:', err);
            setError(err?.message || 'Failed to render chart');
            setParsed(false);
        }
    }, [optionString]);

    if (error) {
        // 流式输出中 JSON 可能不完整 — 优雅降级为灰色代码块
        return (
            <div style={{
                margin: '12px 0',
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                position: 'relative',
            }}>
                <div style={{
                    position: 'absolute', top: '8px', right: '12px',
                    fontSize: '11px', color: '#9CA3AF', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '4px',
                }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} />
                    ECHARTS
                </div>
                <pre style={{
                    margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px',
                    color: '#374151', fontFamily: 'monospace',
                }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    return (
        <div style={{
            margin: '16px 0',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            overflow: 'hidden',
            background: '#FAFAFA',
            position: 'relative',
        }}>
            {/* 标识标签 */}
            <div style={{
                position: 'absolute', top: '8px', right: '12px', zIndex: 10,
                fontSize: '11px', color: '#9CA3AF', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '4px',
                background: 'rgba(250, 250, 250, 0.85)', padding: '2px 8px',
                borderRadius: '4px', backdropFilter: 'blur(4px)',
            }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3B82F6', display: 'inline-block' }} />
                ECHARTS
            </div>

            {/* 图表容器 */}
            <div
                id={`echarts-${uniqueId}`}
                ref={chartRef}
                style={{
                    width: '100%',
                    height: '360px',
                    padding: '8px',
                }}
            />

            {/* 加载提示 */}
            {!parsed && !error && (
                <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#9CA3AF', fontSize: '13px',
                }}>
                    正在渲染图表...
                </div>
            )}
        </div>
    );
};

export default EChartsRenderer;
